import { useState } from "react";
import { z } from "zod";
import {
  MiButton,
  MiDetailFields,
  MiIcon,
  MiList,
  MiModal,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  ENTITY_NAMES,
  ICONS_WEB,
  INPUT_TYPES,
  PAGINATION,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { mdiPlus } from "@mdi/js";
import * as mdi from "@mdi/js";
import { Box, useTheme } from "@mui/material";

export const IssueTemplatesTab = ({
  issueTemplates,
  loading,
  onUpdate,
  currentIssueTemplates = [],
}) => {
  const [openAddModal, setOpenAddModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const theme = useTheme();

  const issueTemplateFieldset = [
    {
      field: "name",
      headerName: ENTITY_NAMES.ISSUE_TEMPLATE,
      headerAlign: "left",
      align: "left",
      groupable: false,
      flex: 1,
    },
  ];

  const edit = useEdit({ issueTemplate: null });

  const issueTemplateFields = [
    {
      label: "Template Name",
      name: "issueTemplate",
      width: "100%",
      fieldType: INPUT_TYPES.SELECT,
      options: issueTemplates,
      validationRule: z
        .string()
        .min(2, `${ENTITY_NAMES.ISSUE_TEMPLATE} is required`),
    },
  ];

  edit.setValidation(issueTemplateFields);

  const handleUpdate = () => {
    onUpdate([
      ...(currentIssueTemplates?.map((item) => item.id) || []),
      edit.getValue("issueTemplate"),
    ]);
    edit.reset();
    setOpenAddModal(false);
  };

  const getRowActions = (row) => {
    return [
      {
        text: BUTTON_LABELS.DELETE,
        onClick: () => {
          setIsOpenDeleteModal(true);
          setSelectedRow(row);
        },
        renderIcon: () => (
          <MiIcon
            path={mdi[ICONS_WEB.TRASH_CAN]}
            size={1}
            color={theme.palette.red.main}
          />
        ),
      },
    ];
  };

  const onDelete = () => {
    onUpdate(
      currentIssueTemplates
        .filter((item) => item.id !== selectedRow.id)
        .map((item) => item.id)
    );
    setIsOpenDeleteModal(false);
    setSelectedRow(null);
    edit.reset();
  };

  const onDeleteCancel = () => {
    setIsOpenDeleteModal(false);
    setSelectedRow(null);
    edit.reset();
  };

  const onCloseModal = () => {
    setOpenAddModal(false);
    edit.reset();
  };

  const openModal = () => setOpenAddModal(true);

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" marginBottom={4}>
        <MiButton
          title={`Add ${ENTITY_NAMES.ISSUE_TEMPLATE}`}
          variant={BUTTON_VARIANTS.CONTAINED}
          startIcon={{
            path: mdiPlus,
            iconSize: 0.8,
          }}
          data-testid="add-assignment-template-btn"
          onClick={openModal}
        />
      </Box>
      <MiList
        columns={issueTemplateFieldset}
        data={currentIssueTemplates}
        paginationMode={PAGINATION.CLIENT}
        loading={loading}
        getRowActions={getRowActions}
        disableRowSelectionOnClick
        data-testid="task-templates-table"
      />
      <MiModal
        open={openAddModal}
        closeModal={onCloseModal}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            size: "medium",
            color: BUTTON_COLORS.BLACK,
            onClick: onCloseModal,
          },
          {
            name: "Submit",
            variant: BUTTON_VARIANTS.CONTAINED,
            size: "medium",
            color: BUTTON_COLORS.PRIMARY,
            disabled: !edit.isValid(),
            onClick: handleUpdate,
          },
        ]}
        loading={false}
        title={`Add ${ENTITY_NAMES.ISSUE_TEMPLATE}`}
        topRightClose={onCloseModal}
      >
        <MiDetailFields detailFields={issueTemplateFields} edit={edit} />
      </MiModal>
      <MiModalConfirm
        title="Confirm Remove"
        description={`Are you sure you want to remove from this task type: ${selectedRow?.name}?`}
        open={isOpenDeleteModal}
        handlePositive={onDelete}
        handleNegative={onDeleteCancel}
        textPositive={BUTTON_LABELS.REMOVE}
      />
    </Box>
  );
};
